import { HelixIcon } from '@helix/helix-icon'
import {
  building_condo as BuildingIcon,
  building_modern as BuildingModernIcon,
  shared as SharedIcon,
  map_pin as MapPinIcon
} from '@helix/helix-icon/outlined'
import { OrgOption, ShareTarget } from './types'

export function getLocationIcon(type: string) {
  switch (type.toLowerCase()) {
    case 'all':
      return null
    case 'account':
      return <HelixIcon icon={BuildingIcon} />
    case 'region':
      return <HelixIcon icon={MapPinIcon} />
    case 'brokerage':
      return <HelixIcon icon={BuildingIcon} />
    case 'office':
      return <HelixIcon icon={BuildingModernIcon} />
    default:
    case 'team':
      return <HelixIcon icon={SharedIcon} />
  }
}

export function mapOptionToShareTarget(option: OrgOption): ShareTarget {
  return {
    id: option.value.id.split('-')[1],
    type: option.value.type
  }
}

export function mapOptionsToShareTargets(options: OrgOption[]): ShareTarget[] {
  return options.map(mapOptionToShareTarget)
}

export function isClickOutsideException(
  clickedElement: Element | null
): boolean {
  const isModalCloseButton =
    clickedElement?.closest('button')?.querySelector('.sr-only')
      ?.textContent === 'Close Modal'

  const isRemoveAccessClick =
    !!clickedElement?.closest('[role="menuitem"]') ||
    !!clickedElement?.closest('button')?.textContent?.includes('Can Use') ||
    !!clickedElement
      ?.closest('li')
      ?.querySelector('button')
      ?.textContent?.includes('Can Use')

  const isShareButtonClick =
    !!clickedElement?.closest('button')?.textContent?.includes('Share') ||
    (clickedElement?.tagName === 'BUTTON' &&
      !!clickedElement.textContent?.includes('Share'))

  return !!isModalCloseButton || isRemoveAccessClick || isShareButtonClick
}

export function filterSelectionsByBusinessRules(
  selections: OrgOption[]
): OrgOption[] {
  return selections.filter((currentOption, _, arr) => {
    const isPlatform = currentOption.value.type === 'Account'
    const isBrokerage = currentOption.value.type === 'Brokerage'
    const isOffice = currentOption.value.type === 'Office'

    const platformSelected = arr.some((o) => o.value.type === 'Account')
    if (platformSelected && !isPlatform) {
      return false
    }

    const brokerageOrOfficeSelected = arr.some(
      (o) => o.value.type === 'Brokerage' || o.value.type === 'Office'
    )
    if (brokerageOrOfficeSelected && isPlatform) {
      return false
    }

    if (isOffice) {
      const belongsToSelectedBrokerage = arr.some(
        (o) =>
          o.value.type === 'Brokerage' &&
          o.value.id === `b-${currentOption.value.brokerageId}`
      )
      if (belongsToSelectedBrokerage) {
        return false
      }
    }

    if (isBrokerage) {
      const hasChildOfficeSelected = arr.some(
        (o) =>
          o.value.type === 'Office' &&
          `b-${o.value.brokerageId}` === currentOption.value.id
      )
      if (hasChildOfficeSelected) {
        return false
      }
    }

    return true
  })
}

// Hierarchical relationship helpers
export function isAncestor(existing: OrgOption, target: OrgOption): boolean {
  // Account is ancestor of everything
  if (existing.value.type === 'Account' && target.value.type !== 'Account') {
    return true
  }

  // Brokerage is ancestor of its offices
  if (existing.value.type === 'Brokerage' && target.value.type === 'Office') {
    return existing.value.id === `b-${target.value.brokerageId}`
  }

  return false
}

export function isDescendant(existing: OrgOption, target: OrgOption): boolean {
  // Everything is descendant of Account
  if (existing.value.type !== 'Account' && target.value.type === 'Account') {
    return true
  }

  // Office is descendant of its brokerage
  if (existing.value.type === 'Office' && target.value.type === 'Brokerage') {
    return target.value.id === `b-${existing.value.brokerageId}`
  }

  return false
}

export function areSiblings(existing: OrgOption, target: OrgOption): boolean {
  // Offices under the same brokerage are siblings
  if (existing.value.type === 'Office' && target.value.type === 'Office') {
    return existing.value.brokerageId === target.value.brokerageId
  }

  // Brokerages under the same account are siblings (assuming same account context)
  if (
    existing.value.type === 'Brokerage' &&
    target.value.type === 'Brokerage'
  ) {
    return true
  }

  return false
}
